import React, { forwardRef, useId } from 'react';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** The label text for the checkbox */
  label?: React.ReactNode;
  /** Optional description text below the label */
  description?: string;
  /** Whether the checkbox is in an indeterminate state */
  indeterminate?: boolean;
  /** Size variant */
  size?: 'sm' | 'md';
  /** ARIA description ID */
  'aria-describedby'?: string;
}

/**
 * Modern checkbox component with SVG overlay checkmark
 * Based on Tailwind UI patterns
 * 
 * @example
 * ```tsx
 * <Checkbox
 *   id="notifications"
 *   label="Enable notifications"
 *   description="Get notified when something happens"
 *   checked={isChecked}
 *   onChange={(e) => setIsChecked(e.target.checked)}
 * />
 * ```
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      indeterminate = false,
      size = 'md',
      className = '',
      id,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    const checkboxSize = size === 'sm' ? 'size-3.5' : 'size-4';
    const svgSize = size === 'sm' ? 'size-3' : 'size-3.5';
    const autoId = useId();
    const resolvedId = id ?? autoId;
    const descriptionId = description ? `${resolvedId}-description` : ariaDescribedBy;

    const checkboxElement = (
      <div className="flex h-6 shrink-0 items-center">
        <div className="group grid size-4 grid-cols-1">
          <input
            ref={ref}
            id={resolvedId}
            type="checkbox"
            aria-describedby={descriptionId}
            className={`col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-primary checked:bg-primary indeterminate:border-primary indeterminate:bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 dark:border-white/10 dark:bg-white/5 dark:checked:border-primary dark:checked:bg-primary dark:indeterminate:border-primary dark:indeterminate:bg-primary dark:focus-visible:outline-primary dark:disabled:border-white/5 dark:disabled:bg-white/10 dark:disabled:checked:bg-white/10 forced-colors:appearance-auto ${checkboxSize} ${className}`}
            {...props}
          />
          <svg
            fill="none"
            viewBox="0 0 14 14"
            className={`pointer-events-none col-start-1 row-start-1 self-center justify-self-center stroke-primary-foreground group-has-[:disabled]:stroke-gray-950/25 dark:group-has-[:disabled]:stroke-white/25 ${svgSize}`}
          >
            <path
              d="M3 8L6 11L11 3.5"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="opacity-0 group-has-[:checked]:opacity-100"
            />
            <path
              d="M3 7H11"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="opacity-0 group-has-[:indeterminate]:opacity-100"
            />
          </svg>
        </div>
      </div>
    );

    // If no label, return just the checkbox
    if (!label && !description) {
      return checkboxElement;
    }

    // Return checkbox with label and optional description
    return (
      <div className="flex gap-3">
        {checkboxElement}
        <div className="text-sm/6">
          {label && (
            <label
              htmlFor={resolvedId}
              className="font-medium text-foreground cursor-pointer"
            >
              {label}
            </label>
          )}
          {description && (
            <p id={descriptionId} className="text-gray-500 dark:text-gray-400">
              {description}
            </p>
          )}
        </div>
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
